import _ from 'lodash';
import * as loginServices from 'services/login';

import axios from 'axios';
import store from 'store/storeApp';

import * as globalActions from '@redux/global/actions';
import * as dashboardActions from 'pages/dashboard/controllers/actions';
import * as ssoProductActions from 'pages/sso-product/controllers/actions';
import * as navActions from '@redux/branding/actions';
import { clearToken } from 'utils/common/session';
import { isProdEnv } from 'utils/checkDeployEnv';

import { navigateReplace, navigateTo } from 'routes/utils/navigationEvents';

import { TYPE_VIEW_ASSET, ROUTE } from 'static/Constants';

const isProductionEnv = isProdEnv();

/**
 * forward user to location
 * @param {string} path route location
 */
export const forwardTo = (path, options) => {
  navigateTo(path, options);

  //! REMOVE it after demo
  const $mainApplication = document.querySelectorAll('.main__application')[0];
  if (!!$mainApplication) $mainApplication.scrollTop = 0;
};

export const forwardToAndReplace = (path) => {
  navigateReplace(path);
};

export const openNewTab = (url, options) => {
  const cusOptions = {
    isOrigin: true,
    ...options,
  };
  const link = cusOptions?.isOrigin ? window.location.origin + url : url;
  window.open(link);
};
/**
 * * check if routing is being blocked or not
 * @returns
 */
export const checkRouteBlocked = () => window.urlBlocked;

export const getParameterFromQuery = (name, location) => {
  if (!location) location = window.location;
  name = name.replace(/[[\]]/g, '\\$&');
  // let regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)'),
  let regex = new RegExp('[?&]' + name + '=(([^&#].*$))'),
    results = regex.exec(location.search);
  if (!results) return null;
  if (!results[2]) return '';
  return decodeURIComponent(results[2].replace(/\+/g, ' '));
};

export const getReturnUrlFromRequestLoader = (request) => {
  const url = new URL(request.url);
  const searchParams = url.search;

  return getParameterFromQuery('returnUrl', {
    search: searchParams,
  });
};

export const getQueryParamsObject = (location) => {
  const params = {};
  const queryString = location.hash.substring(1);
  const regex = /([^&=]+)=([^&]*)/g;
  let match;

  while ((match = regex.exec(queryString))) {
    params[decodeURIComponent(match[1])] = decodeURIComponent(match[2]);
  }

  return params;
};

export const getParamsFromUrl = (location) => {
  const params = {};
  const queryString = location.search.substring(1);
  const regex = /([^&=]+)=([^&]*)/g;
  let match;

  while ((match = regex.exec(queryString))) {
    params[decodeURIComponent(match[1])] = decodeURIComponent(match[2]);
  }

  return params;
};

export const getParamsObj = (queryString) => {
  const params = Object.fromEntries(new URLSearchParams(queryString));
  return params;
};

// post login process flow
const postFlowOrigin = [
  {
    type: 'waiting',
  },
  {
    type: 'eula-for-mfa',
    route: '/eula',
    action: globalActions.validateEula,
    isForwardToAndReplace: true,
  },
  {
    type: 'login-otp',
    route: '/login-otp',
    isForwardToAndReplace: true,
  },
  {
    type: 'eula',
    route: '/eula',
    action: globalActions.validateEula,
    isForwardToAndReplace: true,
  },
  {
    type: 'check-rivir-tc',
    route: '/check-rivir-tc',
    isForwardToAndReplace: true,
  },
  {
    type: 'renewal-notification',
    route: '/renewal-notification',
    isForwardToAndReplace: true,
  },
  {
    type: 'get-basic-info',
    route: '/get-basic-info',
    isForwardToAndReplace: true,
  },

  {
    type: 'check-credit-card-info',
    route: '/check-credit-card-info',
    isForwardToAndReplace: true,
  },
  {
    type: 'change-password-unlock',
    route: '/change-password',
    isForwardToAndReplace: true,
  },
  {
    type: 'check-authorization',
    route: '/check-authorization',
    isForwardToAndReplace: true,
  },
  {
    type: 'personalization',
    route: '/personalization',
    action: globalActions.validatePersonalization,
    isForwardToAndReplace: true,
  },
  {
    type: 'notification',
    route: '/notification',
    action: globalActions.validateCriticalMessage,
    isForwardToAndReplace: true,
  },
  {
    type: 'prerouting',
    route: '/pre-routing',
    isForwardToAndReplace: true,
  },
  {
    type: 'landingpage',
    route: '/landingPage',
    action: globalActions.validateLandingPage,
    isForwardToAndReplace: true,
  },
];

/**
 * Get the next URL from postFlow without navigating
 * This is the core logic from postFlowRedirect but returns URL instead of navigating
 */
export const getPostFlowUrl = ({ type, returnUrl, dispatch }) => {
  let flowIndex = 0,
    flow;

  const authorizeWalmart = window['authorizeWalmart'];

  if (!returnUrl) {
    returnUrl = '/';
  }

  if (type) {
    flow = _.find(postFlow, { type: type });
    flowIndex = _.indexOf(postFlow, flow);
  }

  if (type !== 'waiting') {
    let { action } = postFlow?.[flowIndex] || {};
    // Execute action if present
    if (dispatch && action) {
      dispatch(action());
    }
  }

  // get next flow
  if (flowIndex < postFlow.length - 1) {
    flowIndex = flowIndex + 1;
    const flowItem = postFlow[flowIndex];
    const { route: nextRoute } = flowItem || {};

    let nextUrl;

    if (authorizeWalmart?.isWalmartAuthorize) {
      nextUrl = `${nextRoute}${authorizeWalmart?.walmartSearchQuery}`;
    } else {
      nextUrl = `${nextRoute}?returnUrl=${encodeURIComponent(returnUrl)}`;
    }

    return nextUrl;
  } else {
    // End of flow, return final URL
    return returnUrl;
  }
};

const postFlow = postFlowOrigin.filter((flow) => {
  //* remove flow  check-credit-card-info on prod
  return isProductionEnv ? flow.type !== 'check-credit-card-info' : true;
});

export const postFlowRedirect = ({
  type,
  returnUrl,
  dispatch,
  getVerifiedUrl,
}) => {
  let flowIndex = 0,
    flow;

  if (!type) return;

  const authorizeWalmart = window['authorizeWalmart'];

  if (!returnUrl) {
    returnUrl = '/';
  }

  if (type) {
    flow = _.find(postFlow, { type: type });
    flowIndex = _.indexOf(postFlow, flow);
  }
  if (type !== 'waiting') {
    let { action } = postFlow?.[flowIndex] || {};
    // update flow
    if (dispatch && action) {
      dispatch(action());
    }
  }

  // get next flow
  if (flowIndex < postFlow.length - 1) {
    flowIndex = flowIndex + 1;
    // get next route
    const flowItem = postFlow[flowIndex];
    const { route: nextRoute, isForwardToAndReplace } = flowItem || {};

    // move to next
    let nextUrl;

    if (authorizeWalmart?.isWalmartAuthorize) {
      nextUrl = `${nextRoute}${authorizeWalmart?.walmartSearchQuery}`;
    } else {
      nextUrl = `${nextRoute}?returnUrl=${returnUrl}`;
    }

    if (isForwardToAndReplace) {
      forwardToAndReplace(nextUrl);
    } else {
      forwardTo(nextUrl);
    }
  } else {
    returnUrl =
      getVerifiedUrl && typeof getVerifiedUrl === 'function'
        ? getVerifiedUrl(returnUrl, { isLogin: true })
        : returnUrl;

    forwardToAndReplace(`${returnUrl}`);
  }
};

const pathnameAssets = [
  '/digital-media/assets',
  '/digital-media/documents',
  '/digital-media/multi-media',
  '/asset/create',
  '/favorite/favorite-assets',
  '/digital-media',
];

export const navigateToRouteAsset = ({
  typeViewAsset,
  id,
  // isMultipleImport = false,
}) => {
  const pathname = window.location.pathname;

  const isFavoriteRoute = pathname === '/favorite/favorite-assets';

  const isAssetView =
    pathnameAssets.includes(pathname) ||
    isFavoriteRoute ||
    pathname === `/asset/${id}`;

  if (!isAssetView) return;

  if (typeViewAsset === TYPE_VIEW_ASSET.REPLACE_ASSET) {
    if (isFavoriteRoute) {
      forwardTo(`/asset/${id}?replace-asset&isFavorited=true`);
    } else {
      forwardTo(`/asset/${id}?replace-asset`);
    }
  } else if (typeViewAsset === TYPE_VIEW_ASSET.CREATE_ASSET) {
    forwardTo(`/asset/${id}`);
  } else {
    forwardTo(ROUTE.DIGITAL_MEDIA);
  }
};

export const logout = () => {
  const refreshToken = localStorage.getItem('RefreshToken');
  const dispatch = store.dispatch;

  clearToken();
  forwardTo('/login');
  setTimeout(() => {
    loginServices.revokeToken({ token: refreshToken });
    // axios.delete();
    delete axios.defaults.headers.common['Authorization'];
    dispatch(globalActions.updateProgressBarSuccess([]));
    dispatch(globalActions.setSignalRConnectionSuccess(null));
    dispatch(globalActions.toggleChatView(false));
    dispatch(globalActions.toggleEmailView(false));
    dispatch(globalActions.clearOpenItemsRedux());
    dispatch(dashboardActions.resetMeetingDetails());
    dispatch(globalActions.toggleHelpCenter(false));
    dispatch(ssoProductActions.bpSsoProductValidate(false));
    dispatch(ssoProductActions.bpSsoProductValidateDetail(false));
    dispatch(globalActions.handleLogout());
  }, 200);

  localStorage.removeItem('impersonate');
  sessionStorage.removeItem('expandedKey');
};
